<script setup lang="ts">
import type { Announcement } from '@/api/announcement/announcementType';
import { ExternalLink, ImageOff, X } from 'lucide-vue-next';
import { computed, ref } from 'vue';
import { RouterLink } from 'vue-router';
import AnnouncementMarkdown from './AnnouncementMarkdown.vue';

const props = defineProps<{ announcement: Announcement }>();
const emit = defineEmits<{ close: [] }>();
const imageFailed = ref(false);
const isExternalLink = computed(() => /^https?:\/\//i.test(props.announcement.link_url));
</script>

<template>
  <div class="fixed inset-0 z-[70] flex items-center justify-center bg-slate-950/55 p-4 backdrop-blur-sm" role="presentation" @click.self="emit('close')">
    <section class="relative max-h-[min(760px,92vh)] w-full max-w-2xl overflow-y-auto rounded-[1.75rem] bg-white shadow-2xl" role="dialog" aria-modal="true" aria-labelledby="announcement-popup-title">
      <button class="absolute right-4 top-4 z-10 grid size-10 place-items-center rounded-full bg-white/90 text-slate-500 shadow-sm ring-1 ring-slate-200 transition hover:text-slate-950" type="button" aria-label="关闭公告" @click="emit('close')">
        <X class="size-5" />
      </button>

      <div v-if="announcement.image_url && !imageFailed" class="aspect-[16/7] w-full overflow-hidden bg-slate-100">
        <img :src="announcement.image_url" :alt="announcement.title" class="size-full object-cover" @error="imageFailed = true">
      </div>
      <div v-else-if="announcement.image_url" class="flex aspect-[16/7] w-full items-center justify-center bg-slate-100 text-slate-400">
        <ImageOff class="size-8" aria-hidden="true" />
        <span class="sr-only">公告图片暂时无法加载</span>
      </div>

      <div class="space-y-5 p-6 sm:p-8">
        <div>
          <p class="text-xs font-semibold uppercase tracking-[0.22em] text-blue-600">
            {{ announcement.status === 'archived' ? '历史公告' : '最新公告' }}
          </p>
          <h2 id="announcement-popup-title" class="mt-2 pr-10 text-2xl font-bold tracking-tight text-slate-950 sm:text-3xl">
            {{ announcement.title }}
          </h2>
        </div>
        <AnnouncementMarkdown :content="announcement.content" />
        <div class="flex flex-wrap items-center justify-between gap-3 border-t border-slate-100 pt-4">
          <span class="text-xs text-slate-400">{{ announcement.author_name || '平台公告' }}</span>
          <div class="flex items-center gap-2">
            <a v-if="announcement.link_url && isExternalLink" :href="announcement.link_url" target="_blank" rel="noopener noreferrer" class="inline-flex h-10 items-center gap-2 rounded-xl bg-blue-600 px-4 text-sm font-semibold text-white shadow-sm transition hover:bg-blue-500" @click="emit('close')">
              查看详情
              <ExternalLink class="size-4" />
            </a>
            <RouterLink v-else-if="announcement.link_url" :to="announcement.link_url" class="inline-flex h-10 items-center gap-2 rounded-xl bg-blue-600 px-4 text-sm font-semibold text-white shadow-sm transition hover:bg-blue-500" @click="emit('close')">
              查看详情
              <ExternalLink class="size-4" />
            </RouterLink>
            <button type="button" class="h-10 rounded-xl border border-slate-200 px-4 text-sm font-semibold text-slate-600 transition hover:bg-slate-50" @click="emit('close')">
              我知道了
            </button>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>
